import { useEffect, useRef } from 'react';
import useSyncOfflineData from 'src/utils/connectivity/useSyncOfflineData';
import { useToast } from 'src/app/useToast';

const useBackgroundSyncRecovery = () => {
    const { syncOfflineData } = useSyncOfflineData();
    const { toastError } = useToast();
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (!('serviceWorker' in navigator)) return;

        const handleMessage = (event: MessageEvent) => {
            if (event.data?.type !== 'BACKGROUND_SAVE_FAILED') return;

            const errorType = event.data.errorType;
            console.warn('Background save failed:', event.data);

            if (errorType === 'http') {
                // Server rejected the request — notify user, don't retry
                toastError('Save failed — server returned an error. Please try again.');
                return;
            }

            // Network error (or legacy message without errorType) — trigger sync
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
            debounceTimerRef.current = setTimeout(() => {
                syncOfflineData();
                debounceTimerRef.current = null;
            }, 2000);
        };

        navigator.serviceWorker.addEventListener('message', handleMessage);

        return () => {
            navigator.serviceWorker.removeEventListener('message', handleMessage);
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, [syncOfflineData, toastError]);
};

export default useBackgroundSyncRecovery;
